import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "i18n-fe";
import {
  SETUP_ITEM_ORDER,
  detailCells,
  detailColumns,
  detailRowsOf,
  itemConfirmCopy,
  itemFixLabel,
  itemFixPath,
  itemStatus,
  itemTitle,
  openCountOf,
  isItemAlreadyDone,
  rowFixPath,
  canDeclareShowPass,
  settingsPath,
  writeErrorText,
} from "./setupItems";

// #3570 (show-setup workstream) -- the Pre-show view of the Score Admin
// dashboard (mockup approved by Jaybill 2026-10-20; terms in GLOSSARY.md "Show
// setup"). One screen with no scrolling at 1280x800: a header row (heading,
// Setup progress %, the bar, Start show), the To Do list as one table plus a
// collapsed Done list below it (#3624, #3648; 2/3 width), and Good to know in the right third. Starting only changes
// what the dashboard shows: nothing is locked and scoring is never blocked.
//
// Staff errors say what, why and where to fix it (docs/adr/0004): every item
// carries a Fix link to the exact page, and Details lists the rows still wrong.


const errorText = writeErrorText;

// MUI's filled "warning" chip is white on #ed6c02 (about 3:1, too faint for small text):
// the mockup's amber chip is dark amber text on a pale amber fill.
const WARN_CHIP_SX = { bgcolor: "#fff3d6", color: "#8a5300", border: 1, borderColor: "#e9cf93", fontWeight: 500 };

// #3624: one checklist ROW (the checklist is a single MUI Table). `narrow` is below md
// (the Done count column is gone, so the count rides at the start of the reason line);
// `phone` is xs (the actions sit under the name, the Details button is gone, and tapping
// the row opens the Details drawer; Fix stays a button).
function ItemRow({ item, canAdmin, busy, onToggle, onDetails, narrow, phone, t }) {
  const title = itemTitle(t, item.key);
  const cleared = item.handCheck?.state === "cleared";
  const auto = item.done && !item.handChecked;
  const isDone = !!(item.done || item.handChecked);
  const showFraction = (item.total ?? 0) > 1;
  const fraction = showFraction ? t("dashboard.setup.fraction", { fixed: item.fixed, total: item.total }) : null;
  // null = no in-app page (the ids live in FVD): plain text, never a dead link.
  const fixPath = itemFixPath(item);
  const stop = (e) => e.stopPropagation();
  const fix = !auto && !item.handChecked && fixPath && (
    <Button
      component={RouterLink}
      to={fixPath}
      size="small"
      variant="outlined"
      onClick={stop}
      data-testid={`setup-fix-${item.key}`}
    >
      {t("dashboard.setup.fix")}
    </Button>
  );
  const fixInFvd = !auto && !item.handChecked && !fixPath && (
    <Typography variant="caption" color="text.secondary" data-testid={`setup-fix-${item.key}`}>
      {t("dashboard.setup.fixInFvd")}
    </Typography>
  );
  return (
    <TableRow
      hover={phone}
      data-testid={`setup-item-${item.key}`}
      data-done={isDone ? "true" : "false"}
      onClick={phone ? () => onDetails(item.key) : undefined}
      sx={{
        cursor: phone ? "pointer" : undefined,
        bgcolor: cleared ? "error.50" : undefined,
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      {/* #3672: top-aligned so a wrapped row keeps the box on the title line, with a 6px top pad
          so the 42px box centres on the first line. The text cell below keeps the default 16px
          cell padding, and its title line is 22px (subtitle2, 14px * 1.57): 16 + 22/2 = 27 is the
          line centre, and a top-aligned box centres at 42/2 = 21, so the gap is 6. */}
      <TableCell padding="checkbox" sx={{ width: 48, verticalAlign: "top", pt: "6px" }}>
        <Checkbox
          checked={isDone}
          disabled={!canAdmin || auto || busy}
          onChange={() => onToggle(item)}
          onClick={stop}
          slotProps={{ input: { "aria-label": title } }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1}>
          <Typography component="h3" variant="subtitle2" color={isDone ? "text.secondary" : "text.primary"}>
            {title}
          </Typography>
          {item.handChecked && <Chip size="small" color="success" label={t("dashboard.setup.confirmedByHand")} />}
          {cleared && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={t("dashboard.setup.changedSinceConfirmed")}
              data-testid={`setup-cleared-${item.key}`}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" data-testid={`setup-status-${item.key}`}>
          {narrow && fraction ? `${fraction} \u00b7 ` : ""}
          {itemStatus(t, item)}
        </Typography>
        {cleared && (
          <Typography variant="caption" color="error.main">
            {t("dashboard.setup.changedDetail", { count: item.handCheck.addedRowIds?.length ?? 0 })}
          </Typography>
        )}
        {phone && (fix || fixInFvd) && <Box sx={{ mt: 0.75 }}>{fix || fixInFvd}</Box>}
      </TableCell>
      {!narrow && (
        <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          <Typography variant="body2" color="text.secondary" component="span" data-testid={`setup-fraction-${item.key}`}>
            {fraction ?? "\u2013"}
          </Typography>
        </TableCell>
      )}
      {!phone && (
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {fix || fixInFvd}
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={() => onDetails(item.key)}
              data-testid={`setup-details-${item.key}`}
            >
              {t("dashboard.setup.details")}
            </Button>
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
}

function DetailsDrawer({ item, onClose, t }) {
  const open = !!item;
  const rows = item ? detailRowsOf(item) : [];
  const columns = item ? detailColumns(t, item.key) : [];
  return (
    // #3625: above the app bar (AppLayout pins it at 1300 = zIndex.modal), the same as the class
    // drawer. This drawer holds no Select or popover, so there is no menu to lift (+200).
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}>
      {item && (
        <Box
          role="dialog"
          aria-label={itemTitle(t, item.key)}
          data-testid="setup-drawer"
          sx={{ width: { xs: "100vw", sm: 560 }, maxWidth: "100vw", display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box>
              <Typography variant="h6" component="h2">
                {itemTitle(t, item.key)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rows.length === 0 && item.handChecked
                  ? itemStatus(t, item)
                  : rows.length === 0
                    ? t("dashboard.setup.nothingToFix")
                    : itemStatus(t, item)}
              </Typography>
            </Box>
            <Button size="small" onClick={onClose} data-testid="setup-drawer-close">
              {t("dashboard.setup.close")}
            </Button>
          </Stack>
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {rows.length === 0 ? (
              <Typography color="text.secondary">{t("dashboard.setup.nothingToFix")}</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((c) => (
                        <TableCell key={c}>{c}</TableCell>
                      ))}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} data-testid="setup-detail-row">
                        {detailCells(t, item.key, row).map((cell, i) => (
                          <TableCell key={i}>{cell}</TableCell>
                        ))}
                        <TableCell align="right">
                          {rowFixPath(item.key, row) ? (
                            <Link component={RouterLink} to={rowFixPath(item.key, row)} data-testid="setup-detail-fix">
                              {itemFixLabel(t, item.key)}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary" data-testid="setup-detail-fix-fvd">
                              {t("dashboard.setup.fixInFvd")}
                            </Typography>
                          )}
                          {item.key === "federationIds" && canDeclareShowPass(row) && (
                            <Link
                              component={RouterLink}
                              to="/publish-results"
                              display="block"
                              variant="body2"
                              data-testid="setup-detail-showpass"
                            >
                              {t("dashboard.setup.showPassLink")}
                            </Link>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            {rows.length > 0 && itemFixPath(item) && (
              <Button component={RouterLink} to={itemFixPath(item)} variant="outlined">
                {itemFixLabel(t, item.key)}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}

function GoodToKnow({ info, t }) {
  const onOff = (on) => (on ? t("dashboard.setup.on") : t("dashboard.setup.off"));
  const rows = [
    {
      key: "writeBack",
      anchor: "show-config-card",
      label: t("dashboard.setup.good.writeBack"),
      value: onOff(!info.noFoxbox),
      color: info.noFoxbox ? "default" : "success",
    },
    {
      key: "autoPlace",
      anchor: "auto-place-classes-card",
      label: t("dashboard.setup.good.autoPlace"),
      value: onOff(info.autoPlaceClasses),
      color: info.autoPlaceClasses ? "success" : "default",
    },
    {
      key: "timezone",
      label: t("dashboard.setup.good.timezone"),
      value: info.venueTimezone || t("dashboard.setup.notSet"),
      color: info.venueTimezone ? "success" : "warning",
    },
    {
      key: "sections",
      anchor: "show-section-settings-card",
      label: t("dashboard.setup.good.sections"),
      value: info.sectionLabel || t("dashboard.setup.notUsed"),
      color: "default",
    },
    {
      key: "instant",
      label: t("dashboard.setup.good.instant"),
      value: info.instantUpdates == null ? t("dashboard.setup.notAvailable") : onOff(info.instantUpdates),
      color: info.instantUpdates ? "success" : "default",
    },
    {
      key: "pin",
      anchor: "scribe-pin-card",
      label: t("dashboard.setup.good.pin"),
      value: info.showPinSet ? t("dashboard.setup.set") : t("dashboard.setup.notSet"),
      color: info.showPinSet ? "success" : "warning",
    },
  ];
  return (
    <Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500, pb: 0.5 }}>
        {t("dashboard.setup.goodToKnow")}{" "}
        <Typography component="span" variant="caption" color="text.secondary">
          {t("dashboard.setup.notCounted")}
        </Typography>
      </Typography>
      <Paper variant="outlined" sx={{ px: 1.75, py: 0.5 }} component="aside" aria-label={t("dashboard.setup.goodToKnow")}>
        <Box data-testid="setup-good-to-know">
          {rows.map((r) => (
            <Stack
              key={r.key}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{ py: 0.75, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}
              data-testid={`setup-good-${r.key}`}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {r.label}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
                <Chip
                  size="small"
                  color={r.color === "warning" ? "default" : r.color}
                  variant={r.color === "default" ? "outlined" : "filled"}
                  sx={r.color === "warning" ? WARN_CHIP_SX : undefined}
                  label={r.value}
                />
                <Link component={RouterLink} to={settingsPath(r.anchor)} variant="body2">
                  {t("dashboard.setup.settingsLink")}
                </Link>
              </Stack>
            </Stack>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

function HandCheckDialog({ item, onCancel, onConfirm, busy, error, t }) {
  if (!item) return null;
  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth aria-labelledby="setup-check-title">
      <DialogTitle id="setup-check-title">
        {t("dashboard.setup.checkTitle", { title: itemTitle(t, item.key) })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography>{t("dashboard.setup.checkStillOpen", { count: openCountOf(item) })}</Typography>
          <Alert severity="warning" icon={false}>
            {itemConfirmCopy(t, item.key)}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.setup.checkUnchecksItself")}
          </Typography>
          {error && <Alert severity="error">{errorText(error, t("dashboard.setup.writeError"))}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t("dashboard.setup.cancel")}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy} data-testid="setup-check-confirm">
          {t("dashboard.setup.markDone")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StartDialog({ openItems, onCancel, onConfirm, busy, error, t }) {
  const anyOpen = openItems.length > 0;
  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth aria-labelledby="setup-start-title">
      <DialogTitle id="setup-start-title">{t("dashboard.setup.startTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {anyOpen ? (
            <Alert severity="warning" icon={false} data-testid="setup-start-open-list">
              <Typography sx={{ fontWeight: 500 }}>{t("dashboard.setup.startOpenHeading", { count: openItems.length })}</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {openItems.map((it) => (
                  <li key={it.key}>{itemTitle(t, it.key)}</li>
                ))}
              </Box>
            </Alert>
          ) : (
            <Typography>{t("dashboard.setup.startAllDone")}</Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.setup.startNote")}
          </Typography>
          {error && <Alert severity="error">{errorText(error, t("dashboard.setup.writeError"))}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t("dashboard.setup.cancel")}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy} data-testid="setup-start-confirm">
          {anyOpen ? t("dashboard.setup.startAnyway") : t("dashboard.setup.startShow")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// #3648: one checklist table. To Do and Done render the same ItemRow.
function ChecklistTable({ testId, label, rows, rowProps }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table aria-label={label} data-testid={testId}>
        <TableBody>
          {rows.map((item) => (
            <ItemRow key={item.key} item={item} {...rowProps} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * @param {object} props
 * @param {string} props.heading the dashboard heading (show name and year)
 * @param {string} [props.subtitle]
 * @param {object} props.setup GET /show/:showId/setup
 * @param {boolean} props.canAdmin holds score:assign_scribes (Start show, hand checks)
 * @param {() => Promise} props.onStart
 * @param {(itemKey: string, checked: boolean) => Promise} props.onHandCheck
 * @param {() => Promise} [props.onRefresh] re-read the setup (the item was fixed since the page loaded)
 */
export default function PreShowDashboard({ heading, subtitle, setup, canAdmin, onStart, onHandCheck, onRefresh }) {
  const { t } = useTranslation("scoreadmin");
  const [dialog, setDialog] = useState(null); // { type: "check", key } | { type: "start" }
  const [drawerKey, setDrawerKey] = useState(null);
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState(null);
  const [doneOpen, setDoneOpen] = useState(false); // #3648: the Done list starts collapsed

  const byKey = Object.fromEntries((setup.checklist ?? []).map((it) => [it.key, it]));
  const items = SETUP_ITEM_ORDER.map((k) => byKey[k]).filter(Boolean);
  // #3648: To Do holds the open items, Done the finished and hand-checked ones. The server's order holds
  // inside each group, and an item moves between them on every re-read (poll, hand check, un-check).
  const isClosed = (it) => !!(it.done || it.handChecked);
  const openItems = items.filter((it) => !isClosed(it));
  const closedItems = items.filter(isClosed);
  const doneCount = closedItems.length;
  const percent = setup.progress?.percent ?? 0;
  const drawerItem = drawerKey ? byKey[drawerKey] : null;
  const checkItem = dialog?.type === "check" ? byKey[dialog.key] : null;

  const close = () => {
    setDialog(null);
    setWriteError(null);
  };
  const run = async (fn) => {
    setBusy(true);
    setWriteError(null);
    try {
      await fn();
      setDialog(null);
    } catch (err) {
      if (isItemAlreadyDone(err)) {
        // The server cleared the item since this page loaded: show its real state, no error.
        await onRefresh?.();
        setDialog(null);
      } else {
        setWriteError(err);
      }
    } finally {
      setBusy(false);
    }
  };
  const onToggle = (item) => {
    if (item.handChecked) {
      // Undoing a hand check needs no confirm: the item simply reads open again.
      run(() => onHandCheck(item.key, false));
    } else if (!item.done) {
      setWriteError(null);
      setDialog({ type: "check", key: item.key });
    }
  };

  const rowProps = { canAdmin, busy, onToggle, onDetails: setDrawerKey, narrow, phone, t };

  return (
    <Box sx={{ p: { xs: 2, md: 2 } }} data-testid="preshow-dashboard">
      <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.5} rowGap={0.5} sx={{ mb: 1 }}>
        <Typography variant="h5" component="h1" color="text.primary" data-testid="dashboard-heading">
          {heading}
        </Typography>
        <Chip size="small" label={t("dashboard.setup.preShow")} sx={WARN_CHIP_SX} data-testid="preshow-chip" />
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: { md: "auto" } }}>
            {subtitle}
          </Typography>
        )}
      </Stack>

      <Paper
        variant="outlined"
        component="section"
        aria-label={t("dashboard.setup.progressTitle")}
        sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2.5, px: 2, py: 1.25, mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: "none" }}>
          <Typography variant="h4" sx={{ fontWeight: 300, fontVariantNumeric: "tabular-nums" }} data-testid="setup-percent">
            {t("dashboard.setup.percent", { percent })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("dashboard.setup.progressTitle")}
            <br />
            <span data-testid="setup-done-count">
              {t("dashboard.setup.doneCount", { done: doneCount, total: items.length })}
            </span>
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label={t("dashboard.setup.progressTitle")}
          sx={{ flex: "1 1 120px", minWidth: 80, height: 12, borderRadius: 6 }}
        />
        {canAdmin && (
          <Button variant="contained" onClick={() => { setWriteError(null); setDialog({ type: "start" }); }} data-testid="setup-start">
            {t("dashboard.setup.startShow")}
          </Button>
        )}
      </Paper>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: -0.75, mb: 1 }}>
        {t("dashboard.setup.autoStartNote")}
      </Typography>

      <Box
        data-testid="setup-columns"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(0, 1fr)" },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="section" aria-label={t("dashboard.setup.todoTitle")}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500, pb: 0.5 }}>
              {t("dashboard.setup.todoTitle")}
            </Typography>
            {openItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" data-testid="setup-todo-empty">
                {t("dashboard.setup.todoEmpty")}
              </Typography>
            ) : (
              <ChecklistTable
                testId="setup-checklist-table"
                label={t("dashboard.setup.todoTitle")}
                rows={openItems}
                rowProps={rowProps}
              />
            )}
          </Box>
          {closedItems.length > 0 && (
            <Box component="section" aria-label={t("dashboard.setup.doneTitle")} sx={{ mt: 1.5 }} data-testid="setup-done-section">
              <Button
                onClick={() => setDoneOpen((o) => !o)}
                aria-expanded={doneOpen}
                aria-controls="setup-done-panel"
                data-testid="setup-done-toggle"
                color="inherit"
                endIcon={
                  <ExpandMoreIcon sx={{ transform: doneOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                }
                sx={{ textTransform: "none", fontWeight: 500, fontSize: "1rem", px: 0.5 }}
              >
                {t("dashboard.setup.doneTitle", { done: closedItems.length })}
              </Button>
              <Box id="setup-done-panel">
                {doneOpen && (
                  <ChecklistTable
                    testId="setup-done-table"
                    label={t("dashboard.setup.doneLabel")}
                    rows={closedItems}
                    rowProps={rowProps}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        <GoodToKnow info={setup.goodToKnow ?? {}} t={t} />
      </Box>

      {!canAdmin && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {t("dashboard.setup.adminOnly")}
        </Typography>
      )}

      <DetailsDrawer item={drawerItem} onClose={() => setDrawerKey(null)} t={t} />
      {checkItem && (
        <HandCheckDialog
          item={checkItem}
          busy={busy}
          error={writeError}
          onCancel={close}
          onConfirm={() => run(() => onHandCheck(checkItem.key, true))}
          t={t}
        />
      )}
      {dialog?.type === "start" && (
        <StartDialog openItems={openItems} busy={busy} error={writeError} onCancel={close} onConfirm={() => run(onStart)} t={t} />
      )}
      {writeError && !dialog && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setWriteError(null)}>
          {errorText(writeError, t("dashboard.setup.writeError"))}
        </Alert>
      )}
    </Box>
  );
}
